import { FC, ReactNode, useState } from 'react';
import {
  Box,
  Select,
  type SelectChangeEvent,
  type SelectProps,
  type SxProps,
  type Theme,
  type TypographyProps,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { CaretDownIcon } from '@phosphor-icons/react';

export interface BorderedSelectProps {
  value: SelectProps['value'];
  onChange: (event: SelectChangeEvent<unknown>, child: ReactNode) => void;
  /** `MenuItem`s for the dropdown. */
  children: ReactNode;
  /** Optional leading icon shown before the value (e.g. a funnel). */
  startIcon?: ReactNode;
  multiple?: boolean;
  displayEmpty?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  minWidth?: number | string;
  renderValue?: SelectProps['renderValue'];
  /** Blue "unsaved" tint used by editable forms (e.g. profile fields). */
  dirty?: boolean;
  /**
   * Visual style of the closed field:
   * - `filter` (default): subtle grey surface, no outer border — for toolbars/filters.
   * - `field`: white surface with a visible border — for form inputs (prerequisites).
   */
  variant?: 'filter' | 'field';
  'aria-label'?: string;
  /** Typography token for the trigger text. Defaults to `textSM_regular`. */
  textVariant?: TypographyProps['variant'];
  /** Extra styles merged into the outer bordered container. */
  sx?: SxProps<Theme>;
}

/**
 * App-wide select/dropdown. The closed field keeps the original subtle look — a
 * soft grey surface with no outer border — while the open menu carries the
 * border and aligns to the field width. Supports an optional leading icon, the
 * "unsaved" blue tint used by editable fields, and multi-select.
 */
export const BorderedSelect: FC<BorderedSelectProps> = observer(
  ({
    value,
    onChange,
    children,
    startIcon,
    multiple,
    displayEmpty,
    disabled,
    fullWidth,
    minWidth = 120,
    renderValue,
    dirty,
    variant = 'filter',
    textVariant = 'textSM_regular',
    sx,
    ...rest
  }) => {
    const isField = variant === 'field';
    // Anchor the dropdown menu to the bordered container (not the inner Select)
    // so the open menu aligns with — and matches the width of — the field.
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

    return (
      <Box
        ref={setAnchorEl}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: startIcon ? 0.75 : 0,
          px: 1.25,
          minHeight: 40,
          borderRadius: 2,
          // `filter` (default): subtle grey surface, no visible outer border
          // (kept at 1px transparent for stable sizing) — the border lives on
          // the open menu. `field`: white surface with a visible border, for
          // form inputs (prerequisites). Editable fields tint blue while unsaved.
          border: 1,
          borderColor: dirty ? 'blue.200' : isField ? 'grey.400' : 'transparent',
          backgroundColor: dirty
            ? 'blue.100'
            : isField
              ? 'background.paper'
              : 'grey.200',
          width: fullWidth ? '100%' : 'auto',
          transition: 'background-color 0.15s ease, border-color 0.15s ease',
          ...(isField && { '&:focus-within': { boxShadow: 2 } }),
          ...sx,
        }}
      >
        {startIcon}
        <Select
          variant="standard"
          disableUnderline
          fullWidth
          value={value}
          multiple={multiple}
          displayEmpty={displayEmpty}
          disabled={disabled}
          renderValue={renderValue}
          onChange={onChange}
          aria-label={rest['aria-label']}
          IconComponent={CaretDownIcon}
          sx={{
            typography: textVariant,
            color: 'text.primary',
            flex: 1,
            minWidth,
            '& .MuiSelect-icon': { color: 'text.secondary', fontSize: 18 },
          }}
          MenuProps={{
            anchorEl: anchorEl,
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            PaperProps: {
              sx: {
                mt: 0.5,
                minWidth: anchorEl?.offsetWidth,
                maxWidth: 400,
                borderRadius: 2,
                boxShadow: 4,
                border: 1,
                borderColor: 'grey.200',
                '& .MuiMenuItem-root': {
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  '& .MuiListItemText-root, & .MuiTypography-root': {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  },
                },
              },
            },
          }}
        >
          {children}
        </Select>
      </Box>
    );
  },
);
